import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, ShieldCheck, Mail, CreditCard, Lock, Globe, Calendar, KeyRound, Sparkles, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../lib/api';

/**
 * Admin-only — quick read-only diagnostics for spotting production drift
 * (fees toggle, payfast mode, resend status, bank encryption, CORS, base URL).
 * Surfaces ONLY booleans + masked identifiers; never raw secrets.
 */

const StatusPill = ({ ok, okLabel = 'Healthy', warnLabel = 'Attention' }) => (
  <span
    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${
      ok
        ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
        : 'bg-amber-50 text-amber-900 border-amber-200'
    }`}
    data-testid={`status-pill-${ok ? 'ok' : 'warn'}`}
  >
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    {ok ? okLabel : warnLabel}
  </span>
);

const Card = ({ icon: Icon, title, children, testid }) => (
  <div
    className="bg-white rounded-2xl p-6 shadow-sm border border-[#E5E9E6]"
    data-testid={testid}
  >
    <div className="flex items-center gap-2.5 mb-4">
      <div className="w-9 h-9 rounded-full bg-[#F4F6F3] flex items-center justify-center">
        <Icon className="w-4 h-4 text-[#1F3D2B]" />
      </div>
      <h3 className="text-base font-semibold text-[#112318]" style={{ fontFamily: 'Playfair Display, serif' }}>
        {title}
      </h3>
    </div>
    <div className="space-y-2.5 text-sm">{children}</div>
  </div>
);

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-center justify-between gap-4">
    <span className="text-[#4A5D50]">{label}</span>
    <span className={`text-[#112318] ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

const DemoDataCard = () => {
  const [seeding, setSeeding] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastResult, setLastResult] = useState(null); // {kind:'seed'|'reset', summary}

  const seed = async () => {
    setSeeding(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/demo/seed`, {}, { withCredentials: true });
      setLastResult({ kind: 'seed', summary: r.data?.summary });
      toast.success('Demo data seeded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not seed demo data.');
    } finally {
      setSeeding(false);
    }
  };

  const reset = async () => {
    setConfirmOpen(false);
    setResetting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/demo/reset`, {}, { withCredentials: true });
      setLastResult({ kind: 'reset', summary: r.data?.seeded, deleted: r.data?.deleted });
      toast.success('Demo data reset to pristine state.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reset demo data.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card icon={Sparkles} title="Demo data tools" testid="diag-demo-data">
      <p className="text-xs text-[#4A5D50] leading-relaxed">
        Create or refresh the three demo accounts (organiser / customer / associate),
        the demo event, banking and two completed orders. Affects only records
        tagged <span className="font-mono">is_demo: true</span> — real users, events,
        orders and banking are never touched.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 pt-1">
        <Button
          onClick={seed}
          disabled={seeding || resetting}
          className="flex-1 rounded-full bg-[#1F3D2B] text-white hover:bg-[#172E20]"
          data-testid="demo-seed-button"
        >
          <Sparkles className={`w-4 h-4 mr-2 ${seeding ? 'animate-pulse' : ''}`} />
          {seeding ? 'Seeding…' : 'Seed demo data'}
        </Button>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={seeding || resetting}
          variant="outline"
          className="flex-1 rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
          data-testid="demo-reset-button"
        >
          <RotateCcw className={`w-4 h-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
          {resetting ? 'Resetting…' : 'Reset demo data'}
        </Button>
      </div>

      {lastResult && (
        <div className="text-[11px] text-[#4A5D50] bg-[#F4F6F3] border border-[#E5E9E6] rounded-lg p-2.5 leading-relaxed" data-testid="demo-last-result">
          {lastResult.kind === 'reset' && lastResult.deleted && (
            <p className="mb-1">
              <strong>Deleted:</strong> {Object.entries(lastResult.deleted).map(([k, v]) => `${v} ${k}`).join(' · ')}
            </p>
          )}
          {lastResult.summary && (
            <>
              <p><strong>Users:</strong> {(lastResult.summary.users || []).join(', ')}</p>
              <p><strong>Event:</strong> {lastResult.summary.event_title}</p>
              <p><strong>Tickets sold:</strong> {lastResult.summary.tickets_sold}/{lastResult.summary.capacity}</p>
              <p><strong>Orders:</strong> {(lastResult.summary.orders || []).length}</p>
            </>
          )}
        </div>
      )}

      {/* Reset confirmation */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="demo-reset-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Reset demo data?
            </DialogTitle>
            <DialogDescription>
              This will delete every record tagged <span className="font-mono">is_demo: true</span> and
              re-create the pristine demo set (3 accounts, 1 event, 2 orders, 6/30 sold, R900 revenue).
              Real users, events, orders, banking and payouts are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={reset}
              className="bg-rose-700 text-white hover:bg-rose-800"
              data-testid="demo-reset-confirm-button"
            >
              Yes, reset demo data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

const ChangePasswordCard = () => {
  const [cur, setCur] = useState('');
  const [nw, setNw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (nw !== confirm) { toast.error('New password and confirmation do not match.'); return; }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/change-password`,
        { current_password: cur, new_password: nw },
        { withCredentials: true });
      toast.success('Password updated. Use it next time you log in.');
      setCur(''); setNw(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card icon={KeyRound} title="Change Password" testid="diag-change-password">
      <form onSubmit={submit} className="space-y-2.5">
        <div>
          <Label className="text-xs">Current password</Label>
          <Input
            type="password"
            value={cur}
            onChange={(e) => setCur(e.target.value)}
            required
            data-testid="cp-current"
          />
        </div>
        <div>
          <Label className="text-xs">New password</Label>
          <Input
            type="password"
            value={nw}
            onChange={(e) => setNw(e.target.value)}
            required
            minLength={8}
            data-testid="cp-new"
          />
        </div>
        <div>
          <Label className="text-xs">Confirm new password</Label>
          <Input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            minLength={8}
            data-testid="cp-confirm"
          />
        </div>
        <Button
          type="submit"
          disabled={saving}
          className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full"
          data-testid="cp-submit"
        >
          {saving ? 'Updating…' : 'Update password'}
        </Button>
        <p className="text-[11px] text-stone-500 text-center pt-1">
          Minimum 8 characters. Rotate the bootstrap admin password before going live.
        </p>
      </form>
    </Card>
  );
};

const AdminDiagnosticsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchDiagnostics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  const fetchDiagnostics = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/diagnostics`, { withCredentials: true });
      setDiag(res.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Could not load diagnostics';
      toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#4A5D50]">Loading diagnostics…</p>
      </div>
    );
  }

  if (!diag) return null;

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-12 px-4" data-testid="admin-diagnostics-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-2 flex items-center gap-2">
              <Activity className="w-3 h-3" /> Admin Diagnostics
            </p>
            <h1
              className="text-3xl font-semibold text-[#112318] mb-1"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="diagnostics-heading"
            >
              Platform Health
            </h1>
            <p className="text-sm text-[#4A5D50]">
              Live read-out of critical config — useful when production drifts unexpectedly.
            </p>
          </div>
          <Button
            onClick={fetchDiagnostics}
            disabled={refreshing}
            className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-5"
            data-testid="refresh-diagnostics-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Booking Fees */}
          <Card icon={ShieldCheck} title="Booking Fees" testid="diag-booking-fees">
            <Row
              label="BOOKING_FEES_ENABLED"
              value={
                <StatusPill
                  ok={diag.booking_fees?.enabled}
                  okLabel="true"
                  warnLabel="false"
                />
              }
            />
            <Row label="Raw value" value={diag.booking_fees?.raw_value} mono />
            {!diag.booking_fees?.enabled && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">
                Fees are currently DISABLED. Paid tickets will not include the curation fee.
              </p>
            )}
          </Card>

          {/* PayFast */}
          <Card icon={CreditCard} title="PayFast" testid="diag-payfast">
            <Row
              label="Mode"
              value={
                <StatusPill
                  ok={diag.payfast?.is_live}
                  okLabel="live"
                  warnLabel={diag.payfast?.mode || 'unknown'}
                />
              }
            />
            {!diag.payfast?.is_live && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">
                PayFast is in sandbox/test mode. Real payments are not processed.
              </p>
            )}
          </Card>

          {/* Resend */}
          <Card icon={Mail} title="Resend (Email)" testid="diag-resend">
            <Row
              label="API key"
              value={<StatusPill ok={diag.resend?.api_key_set} okLabel="set" warnLabel="missing" />}
            />
            <Row label="Sender" value={diag.resend?.sender} mono />
            <Row label="Reply-to" value={diag.resend?.reply_to || '—'} mono />
          </Card>

          {/* Bank encryption */}
          <Card icon={Lock} title="Banking Encryption" testid="diag-bank">
            <Row
              label="BANK_ENCRYPTION_KEY"
              value={<StatusPill ok={diag.bank_encryption?.key_set} okLabel="set" warnLabel="not set" />}
            />
            <Row label="Key length" value={`${diag.bank_encryption?.key_length || 0} chars`} mono />
            {!diag.bank_encryption?.key_set && (
              <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-2">
                Banking encryption key not set. Banking details cannot be saved.
              </p>
            )}
          </Card>

          {/* CORS + Base URL */}
          <Card icon={Globe} title="Network" testid="diag-network">
            <Row label="CORS_ORIGINS" value={diag.cors_origins} mono />
            <Row label="PUBLIC_BASE_URL" value={diag.public_base_url} mono />
          </Card>

          {/* Fetched at */}
          <Card icon={Calendar} title="Snapshot" testid="diag-snapshot">
            <Row label="Fetched at" value={new Date(diag.fetched_at).toLocaleString()} mono />
            <p className="text-xs text-[#4A5D50] mt-2">
              Values reflect the env variables active in this backend process.
              Changes to env require a backend restart to appear here.
            </p>
          </Card>

          {/* Change password — admin self-service rotation */}
          <ChangePasswordCard />

          {/* Demo data tools — admin-only seed/reset for recordings */}
          <DemoDataCard />
        </div>
      </div>
    </div>
  );
};

export default AdminDiagnosticsPage;
